import { useSyncExternalStore } from "react";

export interface RequestActivitySnapshot {
  blocking: boolean;
  visible: boolean;
  /** Ícone global. Só escritas; o GET de status da simulação bloqueia sem ícone. */
  indicator: boolean;
}

const IDLE: RequestActivitySnapshot = { blocking: false, visible: false, indicator: false };

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

type ActivityKind = "write" | "status";

export function isBlockingHttpMethod(method?: string | null): boolean {
  return WRITE_METHODS.has((method ?? "").toUpperCase());
}

/** GET `/api/recursos/simulacoes/ativa` — consulta o rascunho ativo. */
export function isSimulacaoStatusUrl(url?: string | null): boolean {
  if (!url) return false;
  const path = url.split("?")[0] ?? "";
  return /(?:^|\/)api\/recursos\/simulacoes\/ativa\/?$/.test(path);
}

export function isSimulacaoStatusGet(method?: string | null, url?: string | null): boolean {
  return (method ?? "").toUpperCase() === "GET" && isSimulacaoStatusUrl(url);
}

export function isBlockingRequest(method?: string | null, url?: string | null): boolean {
  return isBlockingHttpMethod(method) || isSimulacaoStatusGet(method, url);
}

function activityKind(method?: string | null, url?: string | null): ActivityKind | null {
  if (isBlockingHttpMethod(method)) return "write";
  if (isSimulacaoStatusGet(method, url)) return "status";
  return null;
}

let writes = 0;
let statusGets = 0;
let snapshot: RequestActivitySnapshot = IDLE;
const listeners = new Set<() => void>();

function notify(): void {
  const blocking = writes > 0 || statusGets > 0;
  const indicator = writes > 0;
  snapshot = blocking ? { blocking, visible: true, indicator } : IDLE;
  listeners.forEach((listener) => listener());
}

function bump(kind: ActivityKind, delta: 1 | -1): void {
  if (kind === "write") writes = Math.max(0, writes + delta);
  else statusGets = Math.max(0, statusGets + delta);
}

export const requestActivity = {
  begin(method?: string | null, url?: string | null) {
    const kind = activityKind(method, url);
    if (!kind) return;
    bump(kind, 1);
    notify();
  },
  end(method?: string | null, url?: string | null) {
    const kind = activityKind(method, url);
    if (!kind) return;
    bump(kind, -1);
    notify();
  },
  get(): RequestActivitySnapshot {
    return snapshot;
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  reset() {
    writes = 0;
    statusGets = 0;
    notify();
  },
};

export function useRequestActivity(): RequestActivitySnapshot {
  return useSyncExternalStore(requestActivity.subscribe, requestActivity.get, () => IDLE);
}
